import React, { useState } from "react";
import {
  Box,
  Flex,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Button,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Select,
  useDisclosure,
  Code,
} from "@chakra-ui/react";
import { useQuery } from 'react-query';
import { getWebhookLogs } from '../services/webhookService';
import { getGateways } from '../services/gatewayService';
import Card from '../components/Card/Card';
import CardHeader from '../components/Card/CardHeader';
import CardBody from '../components/Card/CardBody';
import CustomModal from '../components/Modal';
import { usePersistedPagination } from "../hooks/usePersistedPagination";

/** برچسب‌های نوع رخداد وب‌هوک دیجی‌کالا (طبق مستندات سلر آکادمی) */
const EVENT_TYPE_LABELS = {
  product_variant_status_change: 'فعال/غیرفعال شدن تنوع',
  order_shipment_digikala: 'ثبت سفارش ارسال توسط دیجی‌کالا',
  order_shipment_seller: 'ثبت سفارش ارسال توسط فروشنده',
  seller_package_status_change_order_fulfilment: 'محمولهٔ تامین سفارش',
  seller_package_status_change_consignment: 'محمولهٔ انبارش',
  commission_change: 'تغییر کمیسیون گروه کالایی',
  /* سازگاری با مقادیر قدیمی */
  order_shipment: 'تغییر وضعیت ارسال سفارش',
  seller_package_status_change: 'تغییر وضعیت محموله‌ها',
};

const SOURCE_LABELS = {
  digikala: 'دیجی‌کالا',
  basalam: 'باسلام',
};

function WebhookLogs() {
  const textColor = useColorModeValue("gray.700", "white");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const { page: currentPage, setPage: setCurrentPage } = usePersistedPagination("webhookLogsTable", {
    defaultPage: 1,
    defaultLimit: 10,
  });
  const [gatewayFilter, setGatewayFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [eventTypeFilter, setEventTypeFilter] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { data: gatewaysData } = useQuery(['gateways'], getGateways);
  const gateways = gatewaysData?.data || [];

  const { data: logsData, isLoading, error, refetch } = useQuery(
    ['webhook-logs', currentPage, gatewayFilter, sourceFilter, eventTypeFilter],
    () => getWebhookLogs({
      page: currentPage,
      limit: 10,
      ...(gatewayFilter && { gateway: gatewayFilter }),
      ...(sourceFilter && { source: sourceFilter }),
      ...(eventTypeFilter && { eventType: eventTypeFilter }),
    }),
    { keepPreviousData: true, staleTime: 30 * 1000 }
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fa-IR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(date);
  };

  const handleViewDetail = (log) => {
    setSelectedLog(log);
   // console.log(log)
    onOpen();
  };

  const logs = logsData?.data || [];
  const pagination = logsData?.pagination || {};

  if (isLoading) {
    return (
      <Flex direction="column" dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Card>
            <CardBody>
              <Flex justify="center" align="center" minH="400px">
                <Spinner size="xl" color="teal.500" />
                <Text mr={4} fontSize="lg" color={textColor}>
                  در حال بارگذاری لاگ‌ها...
                </Text>
              </Flex>
            </CardBody>
          </Card>
        </Box>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction="column" dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Card>
            <CardBody>
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertTitle>خطا در بارگذاری داده‌ها!</AlertTitle>
                <AlertDescription>
                  {error.message || 'خطا در ارتباط با سرور'}
                </AlertDescription>
              </Alert>
              <Button mt={4} colorScheme="teal" onClick={() => refetch()}>
                تلاش مجدد
              </Button>
            </CardBody>
          </Card>
        </Box>
      </Flex>
    );
  }

  return (
    <>
      <Flex direction="column" dir="rtl">
        <Box mx="auto" p={{ base: "20px", md: "30px" }} pe="20px" minH="100vh">
          <Card>
            <CardHeader>
              <Flex direction="column">
                <Text fontSize="lg" color={textColor} fontWeight="bold" me="auto">
                  لاگ وب‌هوک‌ها
                </Text>
                <Text fontSize="md" fontWeight="400" me="auto" color="gray.400">
                  مشاهدهٔ درخواست‌های وب‌هوک دیجی‌کالا و باسلام ذخیره‌شده
                </Text>
              </Flex>
            </CardHeader>
            <CardBody>
              <Flex direction="column" w="100%">
                <Flex mb={4} gap={4} flexWrap="wrap" align="center">
                  <Select
                    maxW="200px"
                    placeholder="همهٔ درگاه‌ها"
                    value={gatewayFilter}
                    onChange={(e) => {
                      setGatewayFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    {gateways.map((g) => (
                      <option key={g._id} value={g._id}>{g.name}</option>
                    ))}
                  </Select>
                  <Select
                    maxW="180px"
                    placeholder="همهٔ منابع"
                    value={sourceFilter}
                    onChange={(e) => {
                      setSourceFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    <option value="digikala">دیجی‌کالا</option>
                    <option value="basalam">باسلام</option>
                  </Select>
                  <Select
                    maxW="280px"
                    placeholder="همهٔ انواع رخداد"
                    value={eventTypeFilter}
                    onChange={(e) => {
                      setEventTypeFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                  >
                    {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Select>
                  <Button size="sm" onClick={() => refetch()}>
                    بروزرسانی
                  </Button>
                </Flex>
                <Box overflowX="auto">
                  <Table variant="simple" color={textColor}>
                    <Thead>
                      <Tr>
                        <Th borderColor={borderColor}>تاریخ</Th>
                        <Th borderColor={borderColor}>درگاه</Th>
                        <Th borderColor={borderColor}>منبع</Th>
                        <Th borderColor={borderColor}>نوع رخداد</Th>
                        <Th borderColor={borderColor}>کد حسابداری</Th>
                        <Th borderColor={borderColor}>عملیات</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {logs.length === 0 ? (
                        <Tr>
                          <Td colSpan={5} textAlign="center" py={8}>
                            <Text color="gray.400">لاگی یافت نشد. پس از دریافت وب‌هوک از دیجی‌کالا اینجا نمایش داده می‌شود.</Text>
                          </Td>
                        </Tr>
                      ) : (
                        logs.map((log) => (
                          <Tr key={log._id}>
                            <Td borderColor={borderColor}>
                              <Text fontSize="sm" color={textColor}>
                                {formatDate(log.createdAt)}
                              </Text>
                            </Td>
                            <Td borderColor={borderColor}>
                              <Text fontSize="sm" color={textColor}>
                                {log.gateway?.name || '—'}
                              </Text>
                            </Td>
                            <Td borderColor={borderColor}>
                              <Badge
                                bg={log.source === 'basalam' ? '#ff5c35' : log.source === 'digikala' ? '#ef4056' : undefined}
                                colorScheme={log.source !== 'basalam' && log.source !== 'digikala' ? 'gray' : undefined}
                                color={log.source === 'basalam' || log.source === 'digikala' ? 'white' : undefined}
                                fontSize="xs"
                              >
                                {SOURCE_LABELS[log.source] || log.source || '-'}
                              </Badge>
                            </Td>
                            <Td borderColor={borderColor}>
                              <Text fontSize="sm" color={textColor}>
                                {log.source === 'digikala'
                                  ? (EVENT_TYPE_LABELS[log.eventType] || log.eventType)
                                  : log.eventType}
                              </Text>
                            </Td>
                            <Td>
                              {log.payload?.data?.order_items?.[0]?.variant?.supplier_code ?? '—'}
                            </Td>
                            <Td borderColor={borderColor}>
                              <Button
                                size="sm"
                                colorScheme="teal"
                                variant="outline"
                                onClick={() => handleViewDetail(log)}
                              >
                                مشاهده جزئیات
                              </Button>
                            </Td>
                          </Tr>
                        ))
                      )}
                    </Tbody>
                  </Table>
                </Box>
                {pagination.pages > 1 && (
                  <Flex justifyContent="center" alignItems="center" mt={4} gap={2} flexWrap="wrap">
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      isDisabled={currentPage === 1}
                    >
                      اولین
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      isDisabled={currentPage === 1}
                    >
                      قبلی
                    </Button>
                    <Text alignSelf="center" px={2}>
                      صفحه {pagination.page} از {pagination.pages}
                    </Text>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(pagination.pages, p + 1))}
                      isDisabled={currentPage === pagination.pages}
                    >
                      بعدی
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => setCurrentPage(pagination.pages)}
                      isDisabled={currentPage === pagination.pages}
                    >
                      آخرین
                    </Button>
                  </Flex>
                )}
              </Flex>
            </CardBody>
          </Card>
        </Box>
      </Flex>

      <CustomModal
        isOpen={isOpen}
        onClose={onClose}
        title="جزئیات وب‌هوک"
        size="xl"
      >
        {selectedLog && (
          <Box pb={2}>
            <Text fontSize="sm" color="gray.500" mb={2}>
              تاریخ: {formatDate(selectedLog.createdAt)} — درگاه: {selectedLog.gateway?.name || '—'} — منبع: {SOURCE_LABELS[selectedLog.source] || selectedLog.source} — نوع: {selectedLog.source === 'digikala' ? (EVENT_TYPE_LABELS[selectedLog.eventType] || selectedLog.eventType) : selectedLog.eventType}
            </Text>
            <Text fontSize="sm" fontWeight="600" mb={2}>
              Payload (بدنهٔ درخواست):
            </Text>
            <Code
              as="pre"
              display="block"
              p={4}
              borderRadius="md"
              fontSize="xs"
              whiteSpace="pre-wrap"
              wordBreak="break-all"
              overflowX="auto"
              maxH="60vh"
              overflowY="auto"
              bg="gray.50"
              _dark={{ bg: 'gray.800' }}
            >
              {JSON.stringify(selectedLog.payload, null, 2)}
            </Code>
            {selectedLog.headers && Object.keys(selectedLog.headers).length > 0 && (
              <>
                <Text fontSize="sm" fontWeight="600" mt={4} mb={2}>
                  هدرها:
                </Text>
                <Code
                  as="pre"
                  display="block"
                  p={4}
                  borderRadius="md"
                  fontSize="xs"
                  whiteSpace="pre-wrap"
                  wordBreak="break-all"
                  bg="gray.50"
                  _dark={{ bg: 'gray.800' }}
                >
                  {JSON.stringify(selectedLog.headers, null, 2)}
                </Code>
              </>
            )}
          </Box>
        )}
      </CustomModal>
    </>
  );
}

export default WebhookLogs;
